import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Container,
  CssBaseline,
  FormControlLabel,
  Link,
  TextField,
  Typography,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { signInWithEmailAndPassword } from 'firebase/auth';
import SEO from './SEO';
import atact120 from '../assets/atact120.png';
import { accessLevels, versionNumber } from '../common/constants';
import createISODateTime from '../common/createISODateTime';
import debugLog from '../common/debugLog';
import validateString from '../common/validator';
import { firGetUserData, firUpdateLastSignIn, primaryAuth } from '../firebase';
import IBMPlexSans from '../fonts/IBMPlexSans-Regular.ttf';

const theme = createTheme({
  typography: { fontFamily: IBMPlexSans },
});

function Copyright() {
  return (
    <Typography align="center" color="text.secondary" variant="body2">
      {'Copyright © '}
      <Link color="inherit" href="https://atact.org">
        atact.org
      </Link>{' '}
      {new Date().getFullYear()}
    </Typography>
  );
}

export default function SignIn() {
  const [loading, setLoading] = useState(false);
  const [remembered, setRemembered] = useState(true); // default to true for now
  const [signInError, setSignInError] = useState(false);
  const [user, setUser] = useState({ username: '', password: '' });

  const navigate = useNavigate();

  const { username, password } = user;

  // destructure imports
  const { siteAdmin } = accessLevels;
  const { appVersion, dateString } = versionNumber;

  const clearForm = () => {
    setSignInError(false);
    setUser({ username: '', password: '' });
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    setSignInError(false);
    setUser({ ...user, [name]: value });
  };

  const handleSignIn = (auth, nameuser, wordpass) => {
    signInWithEmailAndPassword(auth, nameuser, wordpass)
      .then(async (userCredential) => {
        clearForm();
        debugLog(`${appVersion}_${dateString}`);

        firGetUserData(userCredential.user.uid)
          .then((docData) => {
            const { accessLevel, assignedSite } = docData;

            if (accessLevel <= siteAdmin && validateString(assignedSite)) {
              firUpdateLastSignIn(
                userCredential.user.uid,
                createISODateTime({ separator: '_' }),
              ).then(() => {
                navigate('/home');
              });
            } else {
              setLoading(false);
              setSignInError(true);
              console.warn('-- Not authorised');
            }
          })
          .catch(() => {
            setLoading(false);
            setSignInError(true);
            console.warn('-- No such document');
          });
      })
      .catch((error) => {
        const errorCode = error.code;
        const errorMessage = error.message;
        setLoading(false);
        setSignInError(true);
        console.error(errorCode, errorMessage);
      });
  };

  const onSubmit = async (e) => {
    setLoading(true);
    e.preventDefault(); // prevent the default action associated with the event
    handleSignIn(primaryAuth, username, password);
  };

  return (
    <>
      <SEO
        description="Log in to A TACT Connect Management"
        name="qiwei"
        title="Sign In"
        type="webapp"
      />
      <ThemeProvider theme={theme}>
        <Container component="main" maxWidth="xs">
          <CssBaseline />
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              flexDirection: 'column',
              marginTop: 8,
            }}
          >
            <Avatar
              alt="A TACT Logo"
              src={atact120}
              sx={{ marginTop: 2, backgroundColor: 'transparent' }}
              variant="circular"
            />
            <Typography component="h1" variant="h6">
              A TACT Connect Management
            </Typography>
            <Typography variant="caption">
              {appVersion}_{dateString}
            </Typography>
            <Box component="form" onSubmit={onSubmit}>
              <TextField
                autoComplete="username"
                autoFocus
                error={signInError}
                fullWidth
                id="username"
                label="username"
                margin="normal"
                name="username"
                onChange={handleInput}
                required
                size="medium"
                type="text"
                variant="outlined"
              />
              <TextField
                autoComplete="current-password"
                error={signInError}
                fullWidth
                helperText={
                  signInError
                    ? 'sign in failed; check console for more info'
                    : ''
                }
                id="password"
                label="password"
                margin="normal"
                name="password"
                onChange={handleInput}
                required
                size="medium"
                type="password"
                variant="outlined"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={remembered}
                    color="secondary"
                    disabled
                    id="checkbox"
                    name="remember"
                    onChange={(e) => setRemembered(e.target.checked)}
                  />
                }
                label="Remember Me"
              />
              <Button
                fullWidth
                loading={loading}
                size="medium"
                sx={{ marginTop: 2, marginBottom: 2 }}
                type="submit"
                variant="contained"
              >
                Sign In
              </Button>
            </Box>
          </Box>
          <Copyright sx={{ marginTop: 8, marginBottom: 4 }} />
        </Container>
      </ThemeProvider>
    </>
  );
}
